export const CHECKOUT_URL = "https://bleaching-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/a1d4G6PBkdds4U3PZ4MNMq/MOCKUP_BLEACHING-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/dcBzkzfJQYCCTu9QiPgYKk/VALERIA_MONTES_-_BIOMECANICA.png",
};

export const heroTopics = [
  { icon: "stethoscope", label: "Patient Assessment & Diagnosis" },
  { icon: "flask", label: "Bleaching Agents & Clinical Protocols" },
  { icon: "palette", label: "Shade Evaluation & Discoloration Management" },
  { icon: "shield", label: "Sensitivity & Complication Management" },
  { icon: "clipboard", label: "Practical Clinical Checklists" },
];

export const stats = [
  { value: "6", label: "Specialized clinical volumes" },
  { value: "5", label: "Exclusive practical bonuses" },
  { value: "11", label: "Digital learning resources" },
  { value: "100%", label: "Digital format" },
];

export const beforeItems = [
  "You feel uncertain about which whitening approach to consider for different patients.",
  "You struggle to distinguish discoloration types and their clinical implications.",
  "You are unsure how bleaching agent concentration affects treatment decisions.",
  "You find sensitivity management challenging.",
  "You lack a consistent method for documenting shade changes.",
  "You sometimes struggle to explain realistic outcomes to patients.",
];

export const afterItems = [
  "Follow a structured patient-assessment workflow.",
  "Understand the clinical considerations behind different whitening techniques.",
  "Evaluate discoloration characteristics before selecting an approach.",
  "Recognize sensitivity risks and appropriate management considerations.",
  "Use practical checklists to support treatment planning.",
  "Communicate treatment expectations with greater clarity.",
];

export const painPoints = [
  {
    title: "You Feel Unsure Which Whitening Technique to Choose",
    body: "In-office bleaching? At-home whitening? A combined approach? You understand the available options, but comparing their indications and limitations can be challenging.",
  },
  {
    title: "Some Discoloration Cases Are Difficult to Evaluate",
    body: "Not every stain responds the same way. You want to better understand the difference between extrinsic stains, intrinsic discoloration, and cases that may require alternative aesthetic treatments.",
  },
  {
    title: "Tooth Sensitivity Is a Constant Concern",
    body: "You want to understand the factors associated with sensitivity and how to evaluate appropriate preventive and management strategies.",
  },
  {
    title: "Shade Assessment Feels Inconsistent",
    body: "Lighting, dehydration, photography, and shade-guide selection can influence how tooth color is evaluated. You need a more consistent documentation workflow.",
  },
  {
    title: "You Want to Understand Bleaching Agents More Clearly",
    body: "Hydrogen peroxide, carbamide peroxide, concentration, exposure time, and different delivery techniques all raise important clinical questions.",
  },
  {
    title: "You Want to Explain Treatment Expectations More Confidently",
    body: "Patients may expect dramatic or permanent whitening results. You want to communicate realistic possibilities, limitations, maintenance needs, and potential side effects clearly.",
  },
];

export const volumes = [
  {
    number: "Volume I",
    accent: "#1d6fd6",
    title: "Patient Assessment & Whitening Diagnosis",
    subtitle: "Understand the Case Before Choosing the Technique",
    intro: "Learn how to approach the initial evaluation of patients seeking professional teeth whitening.",
    items: [
      "Patient history and relevant risk factors.",
      "Extrinsic versus intrinsic discoloration.",
      "Clinical considerations for whitening eligibility.",
      "Existing restorations and their influence on aesthetic planning.",
      "Baseline shade assessment.",
      "Contraindications and treatment limitations.",
      "Establishing realistic patient expectations.",
    ],
    takeaway: "A structured framework for evaluating whitening candidates before considering treatment options.",
  },
  {
    number: "Volume II",
    accent: "#0f9488",
    title: "Professional Whitening Techniques",
    subtitle: "Understand the Different Approaches and Their Clinical Applications",
    intro: "Explore the principles behind commonly used professional whitening techniques.",
    items: [
      "In-office whitening approaches.",
      "Dentist-supervised at-home whitening.",
      "Combined treatment strategies.",
      "Custom-tray considerations.",
      "Clinical isolation and soft-tissue protection.",
      "Technique selection considerations.",
      "Understanding the limitations of different approaches.",
    ],
    takeaway: "A clearer understanding of available whitening methods and the factors that influence their selection.",
  },
  {
    number: "Volume III",
    accent: "#7c3aed",
    title: "Bleaching Agents & Clinical Protocols",
    subtitle: "Understand the Science Behind Whitening Decisions",
    intro: "Develop a stronger foundation in the materials and clinical principles involved in tooth bleaching.",
    items: [
      "Hydrogen peroxide versus carbamide peroxide.",
      "Concentration and exposure-time considerations.",
      "Mechanisms of tooth bleaching.",
      "Factors influencing treatment response.",
      "Product instructions and safety considerations.",
      "Common protocol errors.",
      "Evidence surrounding light-activated whitening.",
    ],
    takeaway: "A practical scientific foundation for evaluating bleaching products and protocols.",
  },
  {
    number: "Volume IV",
    accent: "#db2777",
    title: "Tooth Sensitivity & Complication Management",
    subtitle: "Recognize Risks and Understand Appropriate Clinical Responses",
    intro: "Tooth sensitivity is one of the most frequently discussed adverse effects of bleaching treatments. This volume focuses on understanding potential complications and the clinical considerations involved in managing them.",
    items: [
      "Factors associated with bleaching sensitivity.",
      "Pre-treatment risk assessment.",
      "Desensitizing approaches and their evidence.",
      "Gingival irritation and soft-tissue complications.",
      "Recognizing unexpected treatment responses.",
      "When treatment modification or discontinuation may be appropriate.",
      "Patient education and follow-up considerations.",
    ],
    takeaway: "A more organized approach to understanding whitening-related risks and complications.",
  },
  {
    number: "Volume V",
    accent: "#d97706",
    title: "Challenging Discoloration Cases",
    subtitle: "Understand Why Different Teeth Respond Differently",
    intro: "Not every discoloration case is suitable for the same treatment. Learn how discoloration characteristics influence whitening expectations and clinical decision-making.",
    items: [
      "Age-related discoloration.",
      "Tetracycline-associated discoloration.",
      "Fluorosis-related aesthetic concerns.",
      "Discoloration following dental trauma.",
      "Non-vital tooth discoloration.",
      "Limitations of bleaching in complex cases.",
      "When restorative alternatives may be considered.",
    ],
    takeaway: "A better understanding of challenging discoloration patterns and their treatment considerations.",
  },
  {
    number: "Volume VI",
    accent: "#1d6fd6",
    title: "Whitening Results, Follow-Up & Long-Term Maintenance",
    subtitle: "Evaluate Outcomes and Support Realistic Patient Expectations",
    intro: "A whitening appointment is only one part of the patient experience. Learn how to approach outcome assessment, documentation, follow-up, and long-term maintenance.",
    items: [
      "Post-treatment shade evaluation.",
      "Understanding color rebound.",
      "Clinical photography considerations.",
      "Maintenance and lifestyle factors.",
      "Follow-up assessment.",
      "Patient aftercare communication.",
      "Recognizing when further evaluation is necessary.",
    ],
    takeaway: "A structured framework for evaluating whitening outcomes and discussing maintenance with patients.",
  },
];

export const resources = [
  { icon: "clipboard", title: "Patient Assessment Framework", body: "Review relevant considerations before selecting a whitening approach." },
  { icon: "palette", title: "Shade Evaluation Reference", body: "Understand important factors influencing tooth-color assessment." },
  { icon: "flask", title: "Bleaching Technique Comparison Guides", body: "Compare the principles, indications, and limitations of different approaches." },
  { icon: "shield", title: "Sensitivity Risk Review", body: "Identify relevant factors associated with whitening sensitivity." },
  { icon: "camera", title: "Clinical Documentation Guidance", body: "Learn how to organize baseline and follow-up records." },
];

export const bonuses = [
  {
    number: "Bonus #1",
    title: "Whitening Patient Assessment Toolkit",
    value: 37,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/29db7883-5931-4a26-9e19-8c7423bf3626.jpg",
    description: "A practical collection of assessment templates to help organize the information needed before evaluating a whitening case.",
    items: ["Patient history checklist.", "Discoloration assessment worksheet.", "Risk-factor review.", "Clinical eligibility considerations.", "Treatment-expectation discussion guide."],
    note: "",
  },
  {
    number: "Bonus #2",
    title: "Tooth Sensitivity Management Quick Reference",
    value: 27,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/6bb00a7c-dcf7-44ce-b1e5-62f04b140e20.jpg",
    description: "A focused educational reference covering sensitivity-related considerations during professional whitening.",
    items: ["Sensitivity risk factors.", "Preventive considerations.", "Desensitizing approaches.", "Warning signs requiring reassessment.", "Follow-up documentation checklist."],
    note: "",
  },
  {
    number: "Bonus #3",
    title: "Dental Shade Assessment & Photography Atlas",
    value: 29,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/e0bd3f4e-bbd7-49c6-a9ab-a1ebb4a551de.jpg",
    description: "A visual reference for understanding consistent shade evaluation and clinical photography.",
    items: ["Shade-assessment principles.", "Lighting considerations.", "Common documentation mistakes.", "Before-and-after photography guidance.", "Color-comparison limitations."],
    note: "",
  },
  {
    number: "Bonus #4",
    title: "Whitening Clinical Documentation Workbook",
    value: 24,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/22987400-8fab-4ff0-ab34-514575fb1087.jpg",
    description: "A collection of editable worksheets for organizing whitening-related clinical records.",
    items: ["Initial assessment template.", "Baseline shade documentation.", "Treatment-record worksheet.", "Follow-up evaluation template.", "Maintenance review checklist."],
    note: "",
  },
  {
    number: "Bonus #5",
    title: "Patient Communication & Aftercare Scripts",
    value: 37,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/f9fa5093-a721-4be2-abfc-49da82d385bc.jpg",
    description: "Ready-to-adapt communication templates that help you explain professional whitening clearly.",
    items: ["How to explain treatment limitations.", "How to discuss potential sensitivity.", "How to explain realistic shade expectations.", "Post-treatment care instructions.", "Maintenance and follow-up communication."],
    note: "Templates should be adapted to the patient's needs and applicable professional requirements.",
  },
];

export const offerStack = [
  { label: "DENTAL BLEACHING BLUEPRINT™ — 6-Volume Clinical Library", value: 97, main: true },
  { label: "Whitening Patient Assessment Toolkit", value: 37, main: false },
  { label: "Tooth Sensitivity Management Quick Reference", value: 27, main: false },
  { label: "Dental Shade Assessment & Photography Atlas", value: 29, main: false },
  { label: "Whitening Clinical Documentation Workbook", value: 24, main: false },
  { label: "Patient Communication & Aftercare Scripts", value: 37, main: false },
];

export const offerIncludes = [
  "6 Specialized Clinical Volumes",
  "5 Practical Clinical Bonuses",
  "Digital Learning Resources",
  "Instant Access",
  "Lifetime Access",
  "7-Day Guarantee",
];

export const testimonials = [
  {
    name: "Dr. Isabella Vargas",
    role: "General Dentist — Mexico",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/f3174329-3997-46f5-8acc-350849a1af1a.jpg",
    quote: "I finally have a clear sequence for whitening consultations. Volume I changed how I screen candidates, and the communication scripts made expectation conversations with patients so much easier.",
  },
  {
    name: "Dr. Andrés Molina",
    role: "Restorative Dentist — Colombia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/1462f3b6-755a-42fb-89a4-ce05ccdbf1af.jpg",
    quote: "The comparison between hydrogen and carbamide peroxide is the clearest I've read. Now I choose concentration and exposure time with real clinical reasoning instead of habit.",
  },
  {
    name: "Dr. Lucía Fernández",
    role: "Dental Surgeon — Argentina",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/1c48d54b-ceae-4075-aaf1-373667af3c77.jpg",
    quote: "Sensitivity was always my biggest worry. The quick reference and the pre-treatment risk review helped me plan and follow up with my patients far more consistently.",
  },
  {
    name: "Dr. Mateo Rojas",
    role: "Cosmetic Dentist — Chile",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/af5a762a-e45a-401b-ae5c-60cf76c0f313.jpg",
    quote: "The shade and photography atlas fixed my documentation. My before-and-after records are finally consistent — same lighting, same angles, same shade references.",
  },
  {
    name: "Dr. Carmen Ortega",
    role: "Aesthetic Dentistry — Spain",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/d01a0e4c-fddd-4c66-a982-685576a4e6c3.jpg",
    quote: "Volume V on challenging discoloration was worth it on its own. I now know when bleaching makes sense and when to discuss restorative alternatives with the patient.",
  },
];

export const faqs = [
  {
    q: "How will I receive the DENTAL BLEACHING BLUEPRINT™?",
    a: "Right after completing your purchase, you'll receive an email with instructions to access your complete digital collection.",
  },
  {
    q: "Is this a physical product?",
    a: "No. DENTAL BLEACHING BLUEPRINT™ is a digital educational collection containing six clinical volumes and five complementary bonuses.",
  },
  {
    q: "Can I download the materials?",
    a: "Yes. The collection is provided in downloadable digital format so you can consult it on your phone, tablet, or computer.",
  },
  {
    q: "Is this a one-time payment?",
    a: "Yes. You pay $27 USD once for the complete collection. There are no recurring subscription charges for this product.",
  },
  {
    q: "Is this suitable for general dentists?",
    a: "Yes. The material is designed for dental professionals interested in understanding the clinical principles behind professional tooth whitening. It can also serve as a supplementary educational reference for dentists working in aesthetic and restorative dentistry.",
  },
  {
    q: "Can I use this information with different whitening products?",
    a: "The collection discusses general clinical principles rather than relying on a single commercial whitening brand. Always follow the instructions, indications, contraindications, and safety requirements of the specific products you use.",
  },
  {
    q: "Is the material applicable internationally?",
    a: "The scientific principles discussed are broadly relevant to dental practice. However, bleaching regulations, permitted materials, concentration limits, and professional requirements vary between countries. Always follow your local regulations and professional standards.",
  },
  {
    q: "Does this replace clinical training?",
    a: "No. DENTAL BLEACHING BLUEPRINT™ is an educational reference intended to support professional learning. It does not replace formal dental education, hands-on clinical training, professional judgment, or individualized patient assessment.",
  },
  {
    q: "Will every patient achieve the same whitening results?",
    a: "No. Whitening outcomes vary according to discoloration characteristics, tooth condition, treatment approach, and individual factors. The collection emphasizes understanding these differences and setting realistic expectations.",
  },
  {
    q: "Is there a money-back guarantee?",
    a: "Yes. If you decide the material doesn't meet your expectations within seven days of purchase, contact our support team to request a refund.",
  },
];

export const finalIncludes = [
  { icon: "book", label: "6 Specialized Clinical Volumes" },
  { icon: "gift", label: "5 Exclusive Practical Bonuses" },
  { icon: "stethoscope", label: "Patient Assessment & Whitening Diagnosis" },
  { icon: "flask", label: "Bleaching Techniques & Protocols" },
  { icon: "shield", label: "Sensitivity & Complication Management" },
  { icon: "palette", label: "Shade Assessment & Discoloration Cases" },
  { icon: "clipboard", label: "Clinical Documentation & Follow-Up" },
];

export const recentBuyers = [
  { name: "Valentina G.", country: "Argentina" },
  { name: "Santiago M.", country: "Peru" },
  { name: "Camila R.", country: "Colombia" },
  { name: "Diego H.", country: "Mexico" },
  { name: "Mariana L.", country: "Chile" },
  { name: "Javier P.", country: "Spain" },
  { name: "Daniela S.", country: "Ecuador" },
  { name: "Alejandro T.", country: "Uruguay" },
  { name: "Gabriela F.", country: "Costa Rica" },
  { name: "Fernando C.", country: "Dominican Republic" },
  { name: "Sofía A.", country: "Paraguay" },
  { name: "Ricardo V.", country: "Guatemala" },
  { name: "Paula N.", country: "Bolivia" },
  { name: "Carlos E.", country: "Panama" },
  { name: "Andrea B.", country: "Venezuela" },
];
